import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Bot, Check, ChevronDown, Clock, Coins, Copy, Database, Hash, Sparkles, X, Zap, Search, ImageIcon, TriangleAlert } from "lucide-react";
import { copy, fmtMs, fmtTokens, fmtUsd, useStatus, useToasts } from "./lib";
import { Markdown } from "./markdown";
import type { Execution, ModelInfo, TargetKind } from "../shared/types";

export function cx(...xs: any[]) {
  return xs.filter(Boolean).join(" ");
}

export function Button({
  variant, size, icon, loading, children, className, ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "ghost" | "danger" | "green"; size?: "sm" | "xs"; icon?: React.ReactNode; loading?: boolean }) {
  return (
    <button
      className={cx("btn", variant && `btn-${variant}`, size && `btn-${size}`, !children && "btn-icon", className)}
      disabled={loading || rest.disabled}
      {...rest}
    >
      {loading ? <span className="spinner" /> : icon}
      {children}
    </button>
  );
}

export function KindIcon({ kind, size = "md" }: { kind: TargetKind; size?: "md" | "lg" }) {
  const s = size === "lg" ? 16 : 13;
  return <span className={cx("kind", `kind-${kind}`, size === "lg" && "kind-lg")}>{kind === "agent" ? <Bot size={s} /> : <Sparkles size={s} />}</span>;
}

export function Badge({ tone, children, className }: { tone?: "green" | "amber" | "red" | "blue" | "violet" | "plain"; children: React.ReactNode; className?: string }) {
  return <span className={cx("badge", tone && `badge-${tone}`, className)}>{children}</span>;
}

export const Dot = ({ tone, pulse }: { tone?: "green" | "amber" | "red" | "blue"; pulse?: boolean }) => <span className={cx("dot", tone && `dot-${tone}`, pulse && "dot-pulse")} />;

export const channelTone = (c: string) => (c === "production" ? "green" : c === "staging" ? "amber" : "blue");

export function Seg<T extends string>({ options, value, onChange }: { options: { value: T; label: React.ReactNode; icon?: React.ReactNode }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="seg">
      {options.map((o) => (
        <button key={o.value} className={cx(value === o.value && "on")} onClick={() => onChange(o.value)} type="button">
          {o.icon}
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Switch({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return <button type="button" className={cx("switch", on && "on")} onClick={() => onChange(!on)} aria-pressed={on} />;
}

export function Modal({ open, onClose, title, children, footer, width }: { open: boolean; onClose: () => void; title: React.ReactNode; children: React.ReactNode; footer?: React.ReactNode; width?: number }) {
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={width ? { width: `min(${width}px, calc(100vw - 32px))` } : undefined}>
        <div className="modal-head">
          <div className="modal-title">{title}</div>
          <Button variant="ghost" size="sm" icon={<X size={15} />} onClick={onClose} />
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Drawer({ open, onClose, title, actions, children }: { open: boolean; onClose: () => void; title: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode }) {
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="drawer-wrap" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="drawer">
        <div className="drawer-head">
          <div className="row grow" style={{ minWidth: 0 }}>{title}</div>
          <div className="row">
            {actions}
            <Button variant="ghost" size="sm" icon={<X size={15} />} onClick={onClose} />
          </div>
        </div>
        <div className="drawer-body">{children}</div>
      </aside>
    </div>
  );
}

export function Empty({ icon, title, children, action }: { icon?: React.ReactNode; title: string; children?: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="empty">
      {icon && <div className="empty-icon">{icon}</div>}
      <div className="empty-title">{title}</div>
      {children && <div style={{ maxWidth: 380 }}>{children}</div>}
      {action && <div style={{ marginTop: 6 }}>{action}</div>}
    </div>
  );
}

export const Spinner = () => <span className="spinner" />;

export function CopyButton({ text, label, size = "xs" }: { text: string; label?: string; size?: "xs" | "sm" }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      size={size}
      variant="ghost"
      icon={done ? <Check size={13} /> : <Copy size={13} />}
      onClick={async (e) => {
        e.stopPropagation();
        await copy(text);
        setDone(true);
        setTimeout(() => setDone(false), 1400);
      }}
    >
      {label}
    </Button>
  );
}

// ---------- JSON com cor ----------
export function JsonView({ value }: { value: any }) {
  const html = useMemo(() => highlightJson(value), [value]);
  return <pre className="json" dangerouslySetInnerHTML={{ __html: html }} />;
}

function esc(s: string) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function highlightJson(value: any) {
  const text = typeof value === "string" ? value : JSON.stringify(value, null, 2) ?? "null";
  return esc(text).replace(
    /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false)\b|\bnull\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|[{}\[\],]/g,
    (m, str, colon, bool) => {
      if (str) return colon ? `<span class="j-key">${str}</span><span class="j-punct">${colon}</span>` : `<span class="j-str">${str}</span>`;
      if (bool) return `<span class="j-bool">${m}</span>`;
      if (m === "null") return `<span class="j-null">null</span>`;
      if (/^[{}\[\],]$/.test(m)) return `<span class="j-punct">${m}</span>`;
      return `<span class="j-num">${m}</span>`;
    }
  );
}

export function OutputView({ format, value }: { format: string; value: any }) {
  if (value === null || value === undefined) return <span className="faint">sem saída</span>;
  if (format === "image" && value?.images) {
    return (
      <div className="img-out">
        {value.images.map((img: any) => (
          <a key={img.id} href={img.url} target="_blank" rel="noreferrer">
            <img src={img.url} alt="" />
          </a>
        ))}
        {value.text && <div className="text-out muted">{value.text}</div>}
      </div>
    );
  }
  if (typeof value === "object") return <JsonView value={value} />;
  const s = String(value);
  if (/(^|\n)(#{1,4} |[-*] |\d+\. |\|)|\*\*[^*]+\*\*|`[^`]+`/.test(s)) return <Markdown text={s} />;
  return <div className="text-out">{s}</div>;
}

export function MetaChips({ e, showModel = true }: { e: Execution; showModel?: boolean }) {
  return (
    <>
      {showModel && <span className="chip"><Hash size={11} />{e.model}</span>}
      <span className="chip"><Clock size={11} />{fmtMs(e.latencyMs)}</span>
      {(e.tokensIn !== null || e.tokensOut !== null) && (
        <span className="chip" title="tokens de entrada → saída">{fmtTokens(e.tokensIn)} → {fmtTokens(e.tokensOut)} tok</span>
      )}
      <span className="chip" title={e.costBasis === "subscription" ? "custo equivalente em preço de tabela; pago pela assinatura" : undefined}>
        <Coins size={11} />
        {fmtUsd(e.costUsd, e.costBasis)}
      </span>
      {e.cacheHit && <span className="chip chip-green"><Zap size={11} />cache</span>}
    </>
  );
}

export function PassBar({ passed, total, compact }: { passed: number; total: number; compact?: boolean }) {
  const p = total ? passed / total : 0;
  return (
    <span className="passbar">
      <span className="passbar-track" style={compact ? { width: 48 } : undefined}>
        <span className={cx("passbar-fill", p < 0.5 ? "low" : p < 0.85 ? "mid" : "")} style={{ width: `${p * 100}%` }} />
      </span>
      <span className="mono num" style={{ fontSize: 12 }}>{passed}/{total}</span>
    </span>
  );
}

export function Spark({ values, width = 84, height = 22 }: { values: number[]; width?: number; height?: number }) {
  const max = Math.max(1, ...values);
  const n = Math.max(1, values.length);
  const slot = width / n;
  const bw = Math.max(1.5, slot - 1.2);
  return (
    <svg className="spark" width={width} height={height} aria-hidden>
      {values.map((v, i) => {
        const h = v ? Math.max(2.5, (v / max) * (height - 2)) : 1;
        return <rect key={i} x={i * slot} y={height - h} width={bw} height={h} rx={0.8} fill={v ? "var(--g9)" : "var(--g5)"} />;
      })}
    </svg>
  );
}

export function Field({ label, hint, children, right }: { label: React.ReactNode; hint?: React.ReactNode; children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="field">
      <div className="between">
        <span className="label">{label}</span>
        {right}
      </div>
      {children}
      {hint && <div className="field-hint">{hint}</div>}
    </div>
  );
}

export function ToastHost() {
  const ts = useToasts();
  return (
    <div className="toasts">
      {ts.map((t) => (
        <div key={t.id} className={cx("toast", t.kind === "error" && "error")}>
          {t.kind === "error" ? <TriangleAlert size={15} /> : <Check size={15} />}
          <div className="col" style={{ gap: 2 }}>
            <span>{t.text}</span>
            {t.sub && <span className="sub">{t.sub}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- popover genérico ----------
export function usePopover(initial = false, onClose?: () => void) {
  const [open, setOpenState] = useState(initial);
  const ref = useRef<HTMLDivElement>(null);
  const closeCb = useRef(onClose);
  closeCb.current = onClose;
  const setOpen = (v: boolean) => {
    setOpenState(v);
    if (!v) closeCb.current?.();
  };
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    const k = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", h);
    window.addEventListener("keydown", k);
    return () => {
      document.removeEventListener("mousedown", h);
      window.removeEventListener("keydown", k);
    };
  }, [open]);
  return { open, setOpen, ref };
}

const PROVIDER_LABEL: Record<string, string> = {
  anthropic: "Anthropic · API",
  openai: "OpenAI · API",
  gemini: "Google Gemini · API",
  openrouter: "OpenRouter · API (vários laboratórios)",
  claude: "Claude · assinatura (claude -p)",
  chatgpt: "ChatGPT · assinatura (codex exec)",
  "claude-code": "Claude Code · agente no sandbox",
  codex: "Codex · agente (experimental)",
  fake: "Teste",
};

// Sem busca, cada provedor mostra no máximo isto; o resto aparece ao digitar.
const GROUP_CAP = 14;
const fmtPrice = (n: number) => String(+n.toFixed(3));

export function ModelPicker({ value, onChange, kinds, disabled, align = "left", defaultOpen, onClose }: { value: string; onChange: (v: string) => void; kinds: ModelInfo["kind"][]; disabled?: boolean; align?: "left" | "right"; defaultOpen?: boolean; onClose?: () => void }) {
  const status = useStatus();
  const { open, setOpen, ref } = usePopover(!!defaultOpen, onClose);
  const [q, setQ] = useState("");
  const models = (status?.models ?? []).filter((m) => kinds.includes(m.kind));
  const current = status?.models.find((m) => m.id === value);
  const groups = [...new Set(models.map((m) => m.provider))];
  const filtered = (p: string) => models.filter((m) => m.provider === p && (!q || (m.id + m.label).toLowerCase().includes(q.toLowerCase())));
  const custom = q.includes("/") && !models.some((m) => m.id === q);
  const pState = (p: string) => status?.providers[p];
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" className="btn model-trigger" style={{ width: "100%", justifyContent: "space-between" }} disabled={disabled} onClick={() => setOpen(!open)}>
        <span className="row" style={{ minWidth: 0 }}>
          {current?.kind === "image" ? <ImageIcon size={14} className="faint" /> : current?.kind === "agent" ? <Bot size={14} className="faint" /> : <Sparkles size={14} className="faint" />}
          <span className="ellipsis">{current?.label ?? value}</span>
          <span className="mono faint ellipsis" style={{ fontSize: 11.5 }}>{value}</span>
        </span>
        <ChevronDown size={14} className="faint" />
      </button>
      {open && (
        <div className="popover" style={{ top: 36, [align]: 0, width: 400, maxHeight: 440, overflow: "auto" } as any}>
          <div className="row" style={{ padding: "4px 6px 8px" }}>
            <Search size={14} className="faint" />
            <input
              autoFocus
              className="input input-sm"
              style={{ border: 0, background: "transparent", boxShadow: "none", padding: 0 }}
              placeholder="Buscar ou digitar provedor/modelo"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && custom) {
                  onChange(q.trim());
                  setOpen(false);
                }
              }}
            />
          </div>
          <div className="pop-sep" />
          {custom && (
            <div className="pop-item" onClick={() => (onChange(q.trim()), setOpen(false))}>
              <span>Usar <span className="mono">{q}</span></span>
            </div>
          )}
          {groups.map((p) => {
            const all = filtered(p);
            if (!all.length) return null;
            const capped = !q && all.length > GROUP_CAP;
            const list = capped ? [...all.slice(0, GROUP_CAP), ...all.slice(GROUP_CAP).filter((m) => m.id === value)] : all;
            const st = pState(p);
            return (
              <div key={p}>
                <div className="pop-group between">
                  <span className="label">{PROVIDER_LABEL[p] ?? p}</span>
                  {st && (
                    <span className="row" style={{ gap: 6, fontSize: 11 }} title={st.lastError ?? st.detail}>
                      <Dot tone={!st.ok ? "red" : st.lastError ? "amber" : "green"} />
                      <span className="faint">{!st.ok ? "indisponível" : st.lastError ? "última chamada falhou" : "ok"}</span>
                    </span>
                  )}
                </div>
                {list.map((m) => (
                  <div key={m.id} className={cx("pop-item", m.id === value && "hl", st && !st.ok && "disabled")} onClick={() => (onChange(m.id), setOpen(false))}>
                    <span className="grow">{m.label}</span>
                    <span className="mono faint" style={{ fontSize: 11 }}>
                      {m.price ? `$${fmtPrice(m.price.in)}/${fmtPrice(m.price.out)}` : m.basis === "subscription" ? "assinatura" : m.basis === "free" ? "grátis" : ""}
                    </span>
                    {m.id === value && <Check size={13} />}
                  </div>
                ))}
                {capped && <div className="pop-more">+{all.length - list.length} modelos · digite para buscar</div>}
              </div>
            );
          })}
          {groups.some((p) => pState(p)?.lastError) && (
            <div className="field-hint" style={{ padding: "8px 9px 4px" }}>
              {groups.filter((p) => pState(p)?.lastError).map((p) => (
                <div key={p}><b>{p}</b>: {pState(p)!.lastError!.slice(0, 140)}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function AutoTextarea({ value, onChange, placeholder, minRows = 2, onKeyDown, className }: { value: string; onChange: (v: string) => void; placeholder?: string; minRows?: number; onKeyDown?: (e: React.KeyboardEvent) => void; className?: string }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(360, el.scrollHeight + 2) + "px";
  }, [value]);
  return <textarea ref={ref} className={cx("textarea", className)} rows={minRows} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} onKeyDown={onKeyDown} />;
}

export const SourceBadge = ({ source }: { source: string }) => {
  const map: Record<string, [string, any]> = { api: ["API", "green"], playground: ["Playground", "violet"], eval: ["Eval", "blue"], judge: ["Juiz", "plain"] };
  const [label, tone] = map[source] ?? [source, "plain"];
  return <Badge tone={tone}>{label}</Badge>;
};

export { Database };
